/**
 * API lib — Module Admin Validation (4.J.2).
 * REFONTE-VALIDATION-COMPLETE — suppression map/create/ignore, ajout refresh + export.
 *
 * Endpoints :
 *   GET  /referentials/skus?q&created_after&page   (created_after optionnel si supporté)
 *   GET  /admin/pdvs?q&page                        (CRUD admin pagine — voir fetchRecentPdvs)
 *   PATCH /admin/skus/:id
 *   PATCH /admin/pdvs/:id
 *   GET  /anomalies?type=NEW_MERCH_DETECTED&status=OPEN   (graceful 404 si absent)
 *   POST /imports/pending-merchs/:anomalyId/resolve
 *
 * Pending-{module} endpoints (REFONTE-VALIDATION-COMPLETE) :
 *   GET  /admin/pending-{m}            — liste
 *   GET  /admin/pending-{m}/count      — badge
 *   POST /admin/pending-{m}/refresh    — auto-résolution
 *   GET  /admin/pending-{m}/export     — CSV download (via fetch+Blob)
 *
 * Phase 4.J.2 · DECISIONS #104
 */

import { apiClient, fetchAuthentifie } from '@/lib/apiClient';
import type { PaginatedResponse, SkuListItem, PdvListItem } from '@/modules/referentials/types/referentials.types';
import type {
  ValidationListFilters,
  MerchAnomaliesResponse,
  PatchSkuPayload,
  PatchSkuSpecsPayload,
  PatchPdvPayload,
  ResolveMerchPayload,
  ResolveMerchResponse,
} from '../types/validation.types';

// ── SKUs récents ──────────────────────────────────────────────────────────────

export async function fetchRecentSkus(
  filters: ValidationListFilters = {},
): Promise<PaginatedResponse<SkuListItem>> {
  const params: Record<string, unknown> = { page: filters.page ?? 1, per_page: 20 };
  if (filters.q?.trim())           params['q']             = filters.q.trim();
  if (filters.created_after)       params['created_after'] = filters.created_after;
  const res = await apiClient.get<PaginatedResponse<SkuListItem>>('/referentials/skus', { params });
  return res.data;
}

// ── PDVs récents ──────────────────────────────────────────────────────────────

export async function fetchRecentPdvs(
  filters: ValidationListFilters = {},
): Promise<PaginatedResponse<PdvListItem>> {
  const params: Record<string, unknown> = { page: filters.page ?? 1, per_page: 20 };
  if (filters.q?.trim()) params['q'] = filters.q.trim();
  // ⚠ `/admin/pdvs` EST LE BON CHEMIN ICI, ET CE N'EST PAS FAUTE D'ALTERNATIVE.
  //   Le commentaire précédent affirmait que `/referentials/pdvs` « n'existe PAS — 404 ».
  //   C'était vrai à l'écriture, c'est FAUX aujourd'hui : la route existe. Mais elle sert
  //   un besoin différent — elle rend les PDV D'UN merchandiser (paramètre obligatoire),
  //   sans recherche `q` ni pagination. Cet écran-ci liste les PDV RÉCEMMENT CRÉÉS, tous
  //   merchandisers confondus, avec recherche : c'est le CRUD admin paginé qu'il lui faut.
  // ⚠ Un commentaire qui affirme sans mesurer envoie le lecteur suivant dans le mur.
  const res = await apiClient.get<PaginatedResponse<PdvListItem>>('/admin/pdvs', { params });
  return res.data;
}

// ── PATCH ─────────────────────────────────────────────────────────────────────

export async function patchSku(
  id: string,
  payload: PatchSkuPayload,
): Promise<SkuListItem> {
  const res = await apiClient.patch<SkuListItem>(`/admin/skus/${id}`, payload);
  return res.data;
}

/** PATCH /admin/skus/:id/specs — merge partiel des 7 specs JSONB (VF.9) */
export async function patchSkuSpecs(
  id: string,
  payload: PatchSkuSpecsPayload,
): Promise<SkuListItem> {
  const res = await apiClient.patch<SkuListItem>(`/admin/skus/${id}/specs`, payload);
  return res.data;
}

/** POST /admin/skus/:id/photo — upload multipart photo SKU (VF.14) */
export async function postSkuPhoto(
  id: string,
  file: File,
): Promise<{ image_url: string; thumbnail_url: string }> {
  const formData = new FormData();
  formData.append('photo', file);
  const res = await apiClient.post<{ image_url: string; thumbnail_url: string }>(
    `/admin/skus/${id}/photo`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data;
}

/** DELETE /admin/skus/:id/photo — supprime la photo SKU (VF.14) */
export async function deleteSkuPhoto(id: string): Promise<void> {
  await apiClient.delete(`/admin/skus/${id}/photo`);
}

export async function patchPdv(
  id: string,
  payload: PatchPdvPayload,
): Promise<PdvListItem> {
  const res = await apiClient.patch<PdvListItem>(`/admin/pdvs/${id}`, payload);
  return res.data;
}

// ── Anomalies Merch ───────────────────────────────────────────────────────────

/**
 * GET /anomalies?type=NEW_MERCH_DETECTED&status=OPEN
 * Endpoint dans spec OpenAPI — pas encore déployé en backend.
 * Le hook gère le 404 gracieusement (empty state neutre).
 */
export async function fetchPendingMerchAnomalies(): Promise<MerchAnomaliesResponse> {
  const res = await apiClient.get<MerchAnomaliesResponse>('/anomalies', {
    params: { type: 'NEW_MERCH_DETECTED', status: 'OPEN' },
  });
  return res.data;
}

// ── Shared types ──────────────────────────────────────────────────────────────

export interface RefreshResult {
  resolved:  number;
  remaining: number;
}

// ── Pending Brands ────────────────────────────────────────────────────────────

export interface PendingBrandItem {
  id:         string;
  rawName:    string;
  batchId:    string | null;
  count:      number;
  status:     string;
  mappedTo:   string | null;
  createdAt:  string;
  resolvedAt: string | null;
}

export interface PendingBrandsListResponse {
  data:  PendingBrandItem[];
  total: number;
}

export async function fetchPendingBrands(
  status = 'PENDING',
): Promise<PendingBrandsListResponse> {
  const res = await apiClient.get<PendingBrandsListResponse>('/admin/pending-brands', {
    params: { status },
  });
  return res.data;
}

export async function fetchPendingBrandsCount(): Promise<{ count: number }> {
  const res = await apiClient.get<{ count: number }>('/admin/pending-brands/count');
  return res.data;
}

export async function refreshPendingBrands(): Promise<RefreshResult> {
  const res = await apiClient.post<RefreshResult>('/admin/pending-brands/refresh');
  return res.data;
}

/** Déclenche le téléchargement XLSX des brands PENDING via une ancre invisible. */
export async function purgeAllPendingBrands(): Promise<{ deleted: number }> {
  const res = await apiClient.delete<{ deleted: number }>('/admin/pending-brands/purge-all');
  return res.data;
}

export function downloadPendingBrandsCsv(): void {
  const baseUrl = (apiClient.defaults.baseURL ?? '').replace(/\/$/, '');
  triggerFileDownload(`${baseUrl}/admin/pending-brands/export`, 'pending-brands.xlsx');
}

// ── Pending SKUs ──────────────────────────────────────────────────────────────

export interface PendingSkuItem {
  id:         string;
  rawName:    string;
  brandRaw:   string | null;
  batchId:    string | null;
  count:      number;
  status:     string;
  mappedTo:   string | null;
  createdAt:  string;
  resolvedAt: string | null;
}

export interface PendingSkusListResponse {
  data:  PendingSkuItem[];
  total: number;
}

export async function fetchPendingSkus(
  status = 'PENDING',
): Promise<PendingSkusListResponse> {
  const res = await apiClient.get<PendingSkusListResponse>('/admin/pending-skus', {
    params: { status },
  });
  return res.data;
}

export async function fetchPendingSkusCount(): Promise<{ count: number }> {
  const res = await apiClient.get<{ count: number }>('/admin/pending-skus/count');
  return res.data;
}

export async function refreshPendingSkus(): Promise<RefreshResult> {
  const res = await apiClient.post<RefreshResult>('/admin/pending-skus/refresh');
  return res.data;
}

export async function purgeAllPendingSkus(): Promise<{ deleted: number }> {
  const res = await apiClient.delete<{ deleted: number }>('/admin/pending-skus/purge-all');
  return res.data;
}

export function downloadPendingSkusCsv(): void {
  const baseUrl = (apiClient.defaults.baseURL ?? '').replace(/\/$/, '');
  triggerFileDownload(`${baseUrl}/admin/pending-skus/export`, 'pending-skus.xlsx');
}

// ── Pending PDVs ──────────────────────────────────────────────────────────────

export interface PendingPdvItem {
  id:         string;
  rawCode:    string;
  rawName:    string | null;
  source:     string;
  batchId:    string | null;
  count:      number;
  status:     string;
  mappedTo:   string | null;
  rawData:    Record<string, unknown> | null;
  createdAt:  string;
  resolvedAt: string | null;
}

export interface PendingPdvsListResponse {
  data:  PendingPdvItem[];
  total: number;
}

export async function fetchPendingPdvs(
  status = 'PENDING',
): Promise<PendingPdvsListResponse> {
  const res = await apiClient.get<PendingPdvsListResponse>('/admin/pending-pdvs', {
    params: { status },
  });
  return res.data;
}

export async function fetchPendingPdvsCount(): Promise<{ count: number }> {
  const res = await apiClient.get<{ count: number }>('/admin/pending-pdvs/count');
  return res.data;
}

export async function refreshPendingPdvs(): Promise<RefreshResult> {
  const res = await apiClient.post<RefreshResult>('/admin/pending-pdvs/refresh');
  return res.data;
}

export async function purgeAllPendingPdvs(): Promise<{ deleted: number }> {
  const res = await apiClient.delete<{ deleted: number }>('/admin/pending-pdvs/purge-all');
  return res.data;
}

export function downloadPendingPdvsCsv(): void {
  const baseUrl = (apiClient.defaults.baseURL ?? '').replace(/\/$/, '');
  triggerFileDownload(`${baseUrl}/admin/pending-pdvs/export`, 'pending-pdvs.xlsx');
}

// ── Pending Merchs ────────────────────────────────────────────────────────────

export interface PendingMerchItem {
  id:            string;
  rawCode:       string;
  batchId:       string | null;
  count:         number;
  status:        string;
  mappedTo:      string | null;
  firstPdvCode:  string | null;
  firstSeenDate: string | null;
  createdAt:     string;
  resolvedAt:    string | null;
}

export interface PendingMerchsListResponse {
  data:  PendingMerchItem[];
  total: number;
}

export async function fetchPendingMerchs(
  status = 'PENDING',
): Promise<PendingMerchsListResponse> {
  const res = await apiClient.get<PendingMerchsListResponse>('/admin/pending-merchs', {
    params: { status },
  });
  return res.data;
}

export async function fetchPendingMerchsCount(): Promise<{ count: number }> {
  const res = await apiClient.get<{ count: number }>('/admin/pending-merchs/count');
  return res.data;
}

export async function refreshPendingMerchs(): Promise<RefreshResult> {
  const res = await apiClient.post<RefreshResult>('/admin/pending-merchs/refresh');
  return res.data;
}

export async function purgeAllPendingMerchs(): Promise<{ deleted: number }> {
  const res = await apiClient.delete<{ deleted: number }>('/admin/pending-merchs/purge-all');
  return res.data;
}

export function downloadPendingMerchsCsv(): void {
  const baseUrl = (apiClient.defaults.baseURL ?? '').replace(/\/$/, '');
  triggerCsvDownload(`${baseUrl}/admin/pending-merchs/export`, '', 'pending-merchs.csv');
}

export async function resolvePendingMerch(
  anomalyId: string,
  payload: ResolveMerchPayload,
): Promise<ResolveMerchResponse> {
  const res = await apiClient.post<ResolveMerchResponse>(
    `/imports/pending-merchs/${anomalyId}/resolve`,
    payload,
  );
  return res.data;
}

// ── Orphan Rawdata ────────────────────────────────────────────────────────────

export interface OrphanRawdataItem {
  id:          string;
  batchId:     string;
  pdvCode:     string;
  visitDate:   string;
  merchRaw:    string | null;
  rawdataRows: number;
  salesTotal:  number | null;
  status:      string;
  resolvedAt:  string | null;
}

export interface OrphanRawdataListResponse {
  data:  OrphanRawdataItem[];
  total: number;
}

export async function fetchOrphanRawdata(
  batchId: string,
  status = 'PENDING',
): Promise<OrphanRawdataListResponse> {
  const res = await apiClient.get<OrphanRawdataListResponse>('/admin/orphan-rawdata', {
    params: { batchId, status },
  });
  return res.data;
}

export async function fetchOrphanRawdataCount(
  batchId: string,
): Promise<{ count: number }> {
  const res = await apiClient.get<{ count: number }>('/admin/orphan-rawdata/count', {
    params: { batchId },
  });
  return res.data;
}

export async function acceptOrphanRawdata(id: string): Promise<OrphanRawdataItem> {
  const res = await apiClient.post<OrphanRawdataItem>(`/admin/orphan-rawdata/${id}/accept`);
  return res.data;
}

export async function rejectOrphanRawdata(id: string): Promise<OrphanRawdataItem> {
  const res = await apiClient.post<OrphanRawdataItem>(`/admin/orphan-rawdata/${id}/reject`);
  return res.data;
}

/**
 * ⚠ `undefined` ET SURTOUT PAS `null` COMME CORPS — LES QUATRE `*-all` DE CE
 *   FICHIER EN DÉPENDENT. Avec `null`, axios considère qu'un corps EXISTE et
 *   applique son défaut par méthode `Content-Type: application/x-www-form-urlencoded`.
 *   Fastify n'a aucun analyseur pour ce type : il répond **415
 *   `FST_ERR_CTP_INVALID_MEDIA_TYPE` AVANT même d'entrer dans le gestionnaire**.
 *   Avec `undefined`, axios n'envoie aucun `Content-Type` et la route répond 200.
 *   Mesuré au chemin réel le 19/08/2026 : `null` → 415, `undefined` → 200.
 * ⚠ MÊME FAMILLE QUE LE CORRECTIF D'`apiClient.ts` (18/08/2026), QUI AVAIT RETIRÉ
 *   L'EN-TÊTE GLOBAL. Retirer le défaut global n'a pas neutralisé le défaut
 *   **par méthode** d'axios : celui-ci ne se déclenche que si un corps est fourni,
 *   et `null` EST un corps fourni. Les boutons de ligne, qui n'en passent aucun,
 *   fonctionnaient — d'où un écran où l'action unitaire marche et l'action de
 *   masse échoue en silence.
 */
export async function acceptAllOrphanRawdata(batchId: string): Promise<{ accepted: number }> {
  const res = await apiClient.post<{ accepted: number }>('/admin/orphan-rawdata/accept-all', undefined, {
    params: { batchId },
  });
  return res.data;
}

export async function rejectAllOrphanRawdata(batchId: string): Promise<{ rejected: number }> {
  const res = await apiClient.post<{ rejected: number }>('/admin/orphan-rawdata/reject-all', undefined, {
    params: { batchId },
  });
  return res.data;
}

export function downloadOrphanRawdataCsv(batchId: string): void {
  const baseUrl = (apiClient.defaults.baseURL ?? '').replace(/\/$/, '');
  triggerCsvDownload(`${baseUrl}/admin/orphan-rawdata/export?batchId=${encodeURIComponent(batchId)}`, '', 'orphan-rawdata.csv');
}

// ── Orphan Activity ───────────────────────────────────────────────────────────

export interface OrphanActivityItem {
  id:          string;
  batchId:     string;
  pdvCode:     string;
  visitDate:   string;
  merchCode:   string | null;
  timeMinutes: number | null;
  isClosed:    boolean;
  status:      string;
  resolvedAt:  string | null;
}

export interface OrphanActivityListResponse {
  data:  OrphanActivityItem[];
  total: number;
}

export async function fetchOrphanActivity(
  batchId: string,
  opts?: { status?: string; isClosed?: boolean },
): Promise<OrphanActivityListResponse> {
  const params: Record<string, unknown> = { batchId };
  if (opts?.status !== undefined)   params['status']   = opts.status;
  if (opts?.isClosed !== undefined) params['isClosed'] = String(opts.isClosed);
  const res = await apiClient.get<OrphanActivityListResponse>('/admin/orphan-activity', { params });
  return res.data;
}

export async function fetchOrphanActivityCount(
  batchId: string,
): Promise<{ count: number }> {
  const res = await apiClient.get<{ count: number }>('/admin/orphan-activity/count', {
    params: { batchId },
  });
  return res.data;
}

export async function acceptOrphanActivity(id: string): Promise<OrphanActivityItem> {
  const res = await apiClient.post<OrphanActivityItem>(`/admin/orphan-activity/${id}/accept`);
  return res.data;
}

export async function rejectOrphanActivity(id: string): Promise<OrphanActivityItem> {
  const res = await apiClient.post<OrphanActivityItem>(`/admin/orphan-activity/${id}/reject`);
  return res.data;
}

export async function acceptAllOrphanActivity(batchId: string): Promise<{ accepted: number }> {
  const res = await apiClient.post<{ accepted: number }>('/admin/orphan-activity/accept-all', undefined, {
    params: { batchId },
  });
  return res.data;
}

export async function rejectAllOrphanActivity(batchId: string): Promise<{ rejected: number }> {
  const res = await apiClient.post<{ rejected: number }>('/admin/orphan-activity/reject-all', undefined, {
    params: { batchId },
  });
  return res.data;
}

export function downloadOrphanActivityCsv(batchId: string): void {
  const baseUrl = (apiClient.defaults.baseURL ?? '').replace(/\/$/, '');
  triggerCsvDownload(`${baseUrl}/admin/orphan-activity/export?batchId=${encodeURIComponent(batchId)}`, '', 'orphan-activity.csv');
}

// ── Shared download helper ────────────────────────────────────────────────────

/**
 * Déclenche un téléchargement CSV via fetch + Blob + ancre invisible.
 * Passe le token JWT dans l'en-tête Authorization.
 *
 * BUG-EXPORT-PDV-CSV : le token est lu depuis useUserStore.getState().accessToken
 * (pattern identique à l'intercepteur Axios + downloadTemplate dans le module import).
 * localStorage.getItem('auth_token') renvoyait toujours null car la clé Zustand
 * persist est 'orbicore-user', pas 'auth_token'.
 */
function triggerCsvDownload(url: string, _: string, filename: string): void {
  triggerFileDownload(url, filename)
}

/**
 * Déclenche le téléchargement d'un fichier binaire (CSV ou XLSX) via fetch + Blob.
 * Le MIME type est laissé tel quel par le navigateur (Content-Type renvoyé par
 * le backend) — pas de hardcode côté front. Pattern identique au CSV download.
 */
function triggerFileDownload(url: string, filename: string): void {
  // `#1090` — jeton posé et rafraîchi sur 401 par `fetchAuthentifie`.
  void fetchAuthentifie(url, { method: 'GET' })
    .then((res) => {
      if (!res.ok) throw new Error(`Export échoué (${res.status})`)
      return res.blob()
    })
    .then((blob) => {
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(objectUrl)
    })
    .catch((err: unknown) => {
      console.error('[File export]', err)
    })
}
